import { useCallback, useEffect } from 'react';
import { useReactFlow } from '@xyflow/react';
import { proxy, useSnapshot } from 'valtio';

interface ViewportSnapshot {
  x: number;
  y: number;
  zoom: number;
}

interface NodeCenteringState {
  centeredNodeId: string | null;
  previousViewport: ViewportSnapshot | null;
  centeredViewport: ViewportSnapshot | null;
}

export const nodeCenteringStore = proxy<NodeCenteringState>({
  centeredNodeId: null,
  previousViewport: null,
  centeredViewport: null,
});

/**
 * 节点居中 hook
 * 双击节点时调整 viewport 将节点居中放大
 * 注意：viewport 同步由 React Flow 的 onViewportChange 自然触发，无需手动处理
 */
export function useNodeCentering() {
  const reactFlowInstance = useReactFlow();
  const snap = useSnapshot(nodeCenteringStore);

  const centerNodeById = useCallback(
    (nodeId: string, options: { zoom?: number; duration?: number; fitRatio?: number; maxZoom?: number } = {}) => {
      const { zoom: customZoom, duration = 400, fitRatio = 0.6, maxZoom = 4 } = options;

      const internalNode = reactFlowInstance.getInternalNode(nodeId);
      if (!internalNode) return;

      const { positionAbsolute } = internalNode.internals;
      const { measured } = internalNode.internals.userNode;
      const width = measured?.width ?? 200;
      const height = measured?.height ?? 200;
      const center = {
        x: positionAbsolute.x + width / 2,
        y: positionAbsolute.y + height / 2,
      };

      let zoom = customZoom;
      const wrapper = document.querySelector('.react-flow__renderer') as HTMLElement | null;
      if (zoom === undefined) {
        if (wrapper) {
          const raw = Math.min(wrapper.clientWidth / width, wrapper.clientHeight / height) * fitRatio;
          zoom = Math.max(0.1, Math.min(raw, maxZoom));
        } else {
          zoom = 1.2;
        }
      }

      const current = reactFlowInstance.getViewport();
      nodeCenteringStore.previousViewport = { x: current.x, y: current.y, zoom: current.zoom };
      nodeCenteringStore.centeredViewport = null;
      nodeCenteringStore.centeredNodeId = nodeId;

      const viewportHeight = wrapper?.clientHeight ?? 800;
      const verticalOffset = viewportHeight * (1 - fitRatio) * 0.2;
      reactFlowInstance.setCenter(center.x, center.y + verticalOffset / zoom, { zoom, duration });

      setTimeout(() => {
        if (nodeCenteringStore.centeredNodeId === nodeId) {
          const settled = reactFlowInstance.getViewport();
          const prev = nodeCenteringStore.previousViewport;
          const noChange = prev &&
            Math.abs(settled.x - prev.x) <= 8 &&
            Math.abs(settled.y - prev.y) <= 8 &&
            Math.abs(settled.zoom - prev.zoom) <= 0.05;
          if (noChange) {
            nodeCenteringStore.centeredNodeId = null;
            nodeCenteringStore.previousViewport = null;
          } else {
            nodeCenteringStore.centeredViewport = { x: settled.x, y: settled.y, zoom: settled.zoom };
          }
        }
      }, duration + 50);
    },
    [reactFlowInstance]
  );

  const restorePreviousViewport = useCallback(
    (duration = 350) => {
      const prev = nodeCenteringStore.previousViewport;
      if (!prev) return;

      const current = reactFlowInstance.getViewport();
      const diffSmall =
        Math.abs(current.x - prev.x) <= 100 &&
        Math.abs(current.y - prev.y) <= 100 &&
        Math.abs(current.zoom - prev.zoom) <= 0.15;

      nodeCenteringStore.centeredNodeId = null;
      nodeCenteringStore.previousViewport = null;

      if (diffSmall) {
        reactFlowInstance.fitView({ duration, maxZoom: 4 });
      } else {
        reactFlowInstance.setViewport(prev, { duration });
      }
    },
    [reactFlowInstance]
  );

  return {
    centerNodeById,
    centeredNodeId: snap.centeredNodeId,
    restorePreviousViewport,
  };
}

export function useEscToRestoreViewport() {
  const reactFlowInstance = useReactFlow();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!nodeCenteringStore.centeredNodeId) return;
      const prev = nodeCenteringStore.previousViewport;
      if (!prev) return;

      const current = reactFlowInstance.getViewport();
      const diffSmall =
        Math.abs(current.x - prev.x) <= 8 &&
        Math.abs(current.y - prev.y) <= 8 &&
        Math.abs(current.zoom - prev.zoom) <= 0.05;

      nodeCenteringStore.centeredNodeId = null;
      nodeCenteringStore.centeredViewport = null;
      nodeCenteringStore.previousViewport = null;

      if (diffSmall) {
        reactFlowInstance.fitView({ duration: 350, maxZoom: 4 });
      } else {
        reactFlowInstance.setViewport(prev, { duration: 350 });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [reactFlowInstance]);
}
